import { z } from "zod";
import { ClinicMessageSourceFileSchema } from "./clinic-conversations.js";
import { ExtractedDocumentReportSchema } from "./incoming-documents.js";
import { IdempotencyKeySchema, IdSchema, IsoDateTimeSchema, Sha256Schema } from "./primitives.js";
export const FaxWorkflowOperationSchema = z.enum([
    "get_settings",
    "save_settings",
    "members",
    "recipients",
    "save_recipient",
    "source_file",
    "list",
    "get_receipt",
    "get_transmission",
    "reserve_packet",
    "queue",
    "retry",
    "cancel",
    "resolve",
    "retry_receipt",
]);
export const FaxReceiptStatusSchema = z.enum([
    "received",
    "processing",
    "matching_required",
    "routing_required",
    "filing",
    "filed",
    "nonpatient",
    "misdirected",
    "quarantined",
    "failed",
]);
export const FaxTransmissionStatusSchema = z.enum([
    "draft",
    "queued",
    "submitting",
    "submitted",
    "delivered",
    "failed",
    "uncertain",
    "cancel_requested",
    "cancelled",
]);
export const FaxConnectionSchema = z.object({
    id: IdSchema,
    tenantId: IdSchema,
    siteId: IdSchema,
    version: z.number().int().positive(),
    provider: z.literal("srfax"),
    credentialPrefix: z.string().regex(/^SRFAX_[A-Z0-9_]{1,60}$/),
    inboundEnabled: z.boolean(),
    outboundEnabled: z.boolean(),
    faxAdminId: IdSchema,
    escalationPhysicianId: IdSchema,
    lastPolledAt: IsoDateTimeSchema.nullable(),
    failureCode: z.string().nullable(),
});
export const FaxRecipientSchema = z.object({
    id: IdSchema,
    tenantId: IdSchema,
    siteId: IdSchema,
    version: z.number().int().positive(),
    name: z.string().min(1).max(240),
    faxNumber: z.string().regex(/^\+?[0-9]{10,15}$/),
    createdBy: IdSchema,
    /** Historical audit metadata; never required to prepare or send a fax. */
    verifiedBy: IdSchema.nullable().optional(),
    /** Historical audit metadata; never required to prepare or send a fax. */
    verifiedAt: IsoDateTimeSchema.nullable().optional(),
});
export const FaxGroupSchema = z.object({
    pages: z.array(z.number().int().positive()).min(1).max(1000),
    reports: z.array(ExtractedDocumentReportSchema).max(1000),
    patientId: IdSchema.nullable(),
    ownerId: IdSchema.nullable(),
    copyPhysicianIds: z.array(IdSchema).default([]),
    samePageMixed: z.boolean().default(false),
    identityWarnings: z.array(z.string()).default([]),
    matchingReason: z.string().optional(),
    requiresReview: z.boolean().default(true),
    confirmedBy: IdSchema.nullable().optional(),
    originalId: IdSchema.nullable().optional(),
});
export const FaxReceiptSchema = z.object({
    id: IdSchema,
    tenantId: IdSchema,
    siteId: IdSchema,
    connectionId: IdSchema,
    providerId: z.string(),
    version: z.number().int().positive(),
    status: FaxReceiptStatusSchema,
    receivedAt: IsoDateTimeSchema,
    senderFax: z.string().nullable(),
    bucket: z.literal("fax-quarantine"),
    objectName: z.string(),
    safeBucket: z.literal("fax-receipts"),
    safeObjectName: z.string(),
    safeContentHash: Sha256Schema.nullable(),
    safeByteSize: z.number().int().positive().nullable(),
    contentHash: Sha256Schema.nullable(),
    byteSize: z.number().int().positive().nullable(),
    pageCount: z.number().int().positive().nullable(),
    scanStatus: z.enum(["pending", "clean", "rejected"]),
    groups: z.array(FaxGroupSchema),
    failureCode: z.string().nullable(),
    assignedTo: IdSchema,
    urgent: z.boolean(),
    escalationPhysicianId: IdSchema,
});
export const FaxTransmissionSchema = z.object({
    id: IdSchema,
    tenantId: IdSchema,
    siteId: IdSchema,
    connectionId: IdSchema,
    patientId: IdSchema.nullable(),
    referralId: IdSchema.nullable(),
    recipientId: IdSchema.nullable(),
    recipientFax: z.string(),
    recipientName: z.string(),
    title: z.string(),
    version: z.number().int().positive(),
    status: FaxTransmissionStatusSchema,
    bucket: z.string(),
    objectName: z.string(),
    contentHash: Sha256Schema,
    byteSize: z.number().int().positive(),
    pageCount: z.number().int().positive(),
    correlationId: z.string().max(20),
    providerId: z.string().nullable(),
    failureCode: z.string().nullable(),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
    assignedTo: IdSchema,
});
/** Server-resolved cover identity; never supplied by the sending browser. */
export const ReferralFaxCoverContextSchema = z
    .object({
    clinic: z
        .object({
        clinicName: z.string().min(1),
        organizationName: z.string().optional(),
        addressLines: z.array(z.string()),
        phone: z.string().optional(),
        fax: z.string().optional(),
    })
        .strict(),
    provider: z.object({ name: z.string().min(1), licenseLine: z.string() }).strict(),
    patientName: z.string().min(1),
    recipientName: z.string().min(1),
    recipientFax: z.string().min(1),
    createdAt: IsoDateTimeSchema,
})
    .strict();
export const FaxVersionedMutationSchema = z.object({
    id: IdSchema,
    expectedVersion: z.number().int().positive(),
    idempotencyKey: IdempotencyKeySchema,
});
export const FaxReservePacketSchema = z
    .object({
    patientId: IdSchema.nullable().optional(),
    recipientId: IdSchema,
    recipientVersion: z.number().int().positive(),
    title: z.string().min(1).max(240),
    contentHash: Sha256Schema,
    byteSize: z.number().int().min(1).max(52428800),
    pageCount: z.number().int().min(1).max(1000),
    idempotencyKey: IdempotencyKeySchema,
    sources: z
        .array(z
        .object({
        source: z
            .object({
            kind: z.enum([
                "document",
                "incoming_document",
                "clinic_document",
                "referral_package",
            ]),
            id: IdSchema,
            version: z.number().int().positive().optional(),
        })
            .strict(),
        file: ClinicMessageSourceFileSchema,
    })
        .strict())
        .max(50)
        .optional(),
})
    .strict();
export const FaxResolveReceiptSchema = FaxVersionedMutationSchema.extend({
    disposition: z.enum(["file", "nonpatient", "misdirected"]),
    reason: z.string().min(1).max(2000),
    groups: z
        .array(z
        .object({
        pages: z.array(z.number().int().positive()).min(1).max(1000),
        patientId: IdSchema,
        ownerId: IdSchema.nullable().optional(),
    })
        .strict())
        .max(1000)
        .optional(),
}).strict();
export const FaxSaveSettingsSchema = z
    .object({
    credentialPrefix: z.string().regex(/^SRFAX_[A-Z0-9_]{1,60}$/),
    inboundEnabled: z.boolean(),
    outboundEnabled: z.boolean(),
    faxAdminId: IdSchema,
    escalationPhysicianId: IdSchema,
    expectedVersion: z.number().int().min(0),
    idempotencyKey: IdempotencyKeySchema,
})
    .strict();
export const FaxSaveRecipientSchema = z
    .object({
    id: IdSchema.optional(),
    expectedVersion: z.number().int().positive().optional(),
    name: z.string().min(1).max(240),
    faxNumber: z.string().min(10).max(40),
    idempotencyKey: IdempotencyKeySchema,
})
    .strict();
/*
 * Replying to a received fax (EXECDEV-207). Staff mark up the clinic's safe copy of a received
 * fax (ticks, crosses, typed text such as a date or "Confirmed, thanks", and the signed-in
 * physician's own enrolled signature) and fax it back. The browser only places the marks; the
 * API draws them into the page content of the immutable safe copy, so the exact packet reviewed
 * before sending is the one faxed. Positions are fractions of the page box, measured from its
 * top-left corner; text sizes are PDF points.
 */
const FaxReplyPageSchema = z.number().int().positive().max(1000);
const FaxReplyFractionSchema = z.number().min(0).max(1);
export const FAX_REPLY_LIMITS = { marks: 200, pages: 300, textLength: 500 };
/** Tick and cross sizes: the side of their square, as a fraction of the page width. */
export const FAX_REPLY_INK_SIZE = { min: 0.012, max: 0.12 };
/** Typed text sizes, in PDF points. */
export const FAX_REPLY_FONT_SIZE = { min: 7, max: 28 };
const FaxReplyInkMarkShape = {
    page: FaxReplyPageSchema,
    x: FaxReplyFractionSchema,
    y: FaxReplyFractionSchema,
    size: z.number().min(FAX_REPLY_INK_SIZE.min).max(FAX_REPLY_INK_SIZE.max),
};
export const FaxReplyMarkSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("check"), ...FaxReplyInkMarkShape }).strict(),
    z.object({ kind: z.literal("cross"), ...FaxReplyInkMarkShape }).strict(),
    z
        .object({
        kind: z.literal("text"),
        page: FaxReplyPageSchema,
        x: FaxReplyFractionSchema,
        y: FaxReplyFractionSchema,
        /** Lines are separated by "\n"; text is never wrapped, so what is typed is what prints. */
        text: z.string().min(1).max(FAX_REPLY_LIMITS.textLength),
        fontSize: z.number().min(FAX_REPLY_FONT_SIZE.min).max(FAX_REPLY_FONT_SIZE.max),
    })
        .strict(),
    z
        .object({
        kind: z.literal("signature"),
        page: FaxReplyPageSchema,
        x: FaxReplyFractionSchema,
        y: FaxReplyFractionSchema,
        width: z.number().min(0.04).max(1),
        height: z.number().min(0.01).max(1),
    })
        .strict(),
]);
/**
 * How the browser and the API draw a mark, so the editor shows what the fax will print. Ink
 * points are in the mark's unit square (x right, y down); strokes are fractions of its side.
 * A text line is `lineHeight` times the font size, its baseline `baseline` times the font size
 * below the top of the line.
 */
export const FAX_REPLY_DRAWING = {
    check: {
        points: [
            [0.12, 0.55],
            [0.4, 0.82],
            [0.9, 0.16],
        ],
        stroke: 0.13,
    },
    cross: {
        segments: [
            [
                [0.18, 0.18],
                [0.82, 0.82],
            ],
            [
                [0.82, 0.18],
                [0.18, 0.82],
            ],
        ],
        stroke: 0.12,
    },
    text: { lineHeight: 1.2, baseline: 0.95 },
};
// The Windows-1252 (WinAnsi) characters outside Latin-1 that the fax's standard font can print.
const FAX_REPLY_WIN_ANSI_EXTRA = new Set(Array.from("€‚ƒ„…†‡ˆ‰Š‹ŒŽ‘’“”•–—˜™š›œžŸ").map((character) => character.codePointAt(0)));
/** True when every character of the text can be printed by the fax's standard font. */
export function faxReplyTextPrintable(text) {
    for (const character of text) {
        const code = character.codePointAt(0) ?? 0;
        if (character === "\n")
            continue;
        if ((code >= 0x20 && code <= 0x7e) || (code >= 0xa0 && code <= 0xff))
            continue;
        if (FAX_REPLY_WIN_ANSI_EXTRA.has(code))
            continue;
        return false;
    }
    return true;
}
export const FaxReplyRequestSchema = z
    .object({
    /** The safe copy the marks were placed on (the received fax's `safeContentHash`). */
    receiptContentHash: Sha256Schema,
    recipientId: IdSchema,
    /** The chart the reply is kept with; every included page must be filed to that patient. */
    patientId: IdSchema.optional(),
    title: z.string().trim().min(1).max(160),
    coverNote: z.string().max(8000).default(""),
    /** Received-fax page numbers to send back, in order. */
    pages: z.array(FaxReplyPageSchema).min(1).max(FAX_REPLY_LIMITS.pages),
    marks: z.array(FaxReplyMarkSchema).max(FAX_REPLY_LIMITS.marks),
    /** The reply keeps its own key; its packet reservation uses `<key>:packet`. */
    idempotencyKey: z.string().min(8).max(120),
})
    .strict()
    .superRefine((request, context) => {
    if (new Set(request.pages).size !== request.pages.length)
        context.addIssue({ code: "custom", path: ["pages"], message: "A page is listed twice" });
    request.marks.forEach((mark, index) => {
        if (!request.pages.includes(mark.page))
            context.addIssue({
                code: "custom",
                path: ["marks", index, "page"],
                message: "A mark is on a page that is not being sent",
            });
        if (mark.kind === "text" && !faxReplyTextPrintable(mark.text))
            context.addIssue({
                code: "custom",
                path: ["marks", index, "text"],
                message: "Text contains characters a fax cannot print",
            });
    });
});
export const FaxReplySchema = z
    .object({
    id: IdSchema,
    receiptId: IdSchema,
    transmissionId: IdSchema,
    patientId: IdSchema.nullable(),
    pages: z.array(FaxReplyPageSchema).min(1),
    markCount: z.number().int().nonnegative(),
    signed: z.boolean(),
    createdBy: IdSchema,
    createdByName: z.string().nullable(),
    createdAt: IsoDateTimeSchema,
    status: FaxTransmissionStatusSchema,
    transmissionVersion: z.number().int().positive(),
    recipientName: z.string(),
    recipientFax: z.string(),
    title: z.string(),
    failureCode: z.string().nullable(),
})
    .strict();
export const FaxReplyPreparedSchema = z
    .object({ reply: FaxReplySchema, transmission: FaxTransmissionSchema })
    .strict();
//# sourceMappingURL=fax.js.map